/**
 * @author Maria Osama
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-07-03 12:19:18
 * @description Implementation of the Privacy Policy react component
 * @filename privacy-policy.tsx
 */
import React from "react";
import { Router } from "../../../utilities/router";

/**
 *
 * A React component that renders the Privacy Policy
 *
 * @extends {React.Component}
 */
export class PrivacyPolicy extends React.Component {
  componentDidMount() {
    // check query parameters to scroll to specific section
    Router.getQueryParameters().then((QPS) => {
      let section: string = "";
      for (const qp of QPS) {
        if (qp.indexOf("section") >= 0) {
          section = qp.split("=")[1];
          break;
        }
      }
      if (section) {
        setTimeout(() => {
          Router.scrollTo(section);
        }, 0);
      }
    });
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div id="PrivacyPolicy" className="external inner-pages">
        <h1>Privacy Policy</h1>
        <div className="introduction">
          <h4>
            Radivision, Inc. or radivision.com
            <br />
            Privacy Notice
            <br />
            Effective date: April 17, 2017
          </h4>
          <p>
            Welcome to www.radivision.com (“Site” or “Service”). The Service is
            operated by Radivision, Inc. (“Radivision”, “we”, “our”, or “us”).
          </p>
          <p>
            This Privacy Notice explains how we collect, use, share and protect
            information through the Service, and describes your choices about
            the collection and use of your personal information. It applies only
            to information collected via the Service; it does not apply to any
            other information collected or obtained through other means or to
            information collected by third parties.
          </p>
        </div>
        <div className="privacy-content">
          <ol>
            <li>
              <h5>Consent</h5>
              <span>
                <p>
                  By using the Service, you signify your agreement to the terms
                  of this Privacy Notice. Please ensure that you read this
                  Privacy Notice carefully.
                </p>
                <p>
                  We may modify this Privacy Notice from time to time. We will
                  post any adjustments to this Privacy Notice on this page, and
                  the revised version will be effective when it is posted. If we
                  materially change the ways in which we use or share personal
                  information previously collected from you through the Service,
                  we will notify you through the Service, by email, or other
                  communication. If you do not agree with the proposed changes,
                  you should discontinue your use of radivision.com before the
                  new Privacy Notice takes effect. If you continue using
                  radivision.com after the new terms take effect, you will be
                  bound by the modified Privacy Notice.
                </p>
                <p>
                  If you do not agree with our Privacy Notice, please delete
                  your account and uninstall the Service.
                </p>
              </span>
            </li>

            <li>
              <h5>Information We Collect</h5>
              <span>
                <p>
                  We collect information from users and visitors when they
                  interact with the Service. This includes, for example, the
                  following:
                </p>
                <p>Information We Collect Automatically</p>
                <p>
                  When a user uses, or interacts with our Site the user’s
                  browser automatically provides, and we automatically collect
                  and store, certain information about the user’s device
                  (computer, tablet, smart phone) and the user’s activities.
                  This includes, for example:
                </p>
                <ul>
                  <li>
                    Preferences and settings: time zone, language, and character
                    size;
                  </li>
                  <li>
                    Identifiers: IP address, mobile device advertising
                    identifier, Media Access Control (MAC) address;
                  </li>
                  <li>
                    Technical information: type of device, operating system name
                    and version, device manufacturer, browser information (type,
                    version), screen resolution;
                  </li>
                  <li>
                    Connection: Internet service provider or mobile carrier’s
                    name, connection speed and connection type;
                  </li>
                  <li>
                    Information about use of the Site: data stamp, URL of the
                    last webpage visited before visiting our Site, and URL of
                    the first page; visited after leaving our Site, pages
                    viewed, time spent on a page, click through, clickstream
                    data, queries made, search results selected, comments made,
                    search history, type of service requested, purchases made;
                  </li>
                  <li>
                    Information collected through cookies, pixel tags, and other
                    technologies; and
                  </li>
                  <li>General geographic location.</li>
                </ul>
                <h6>Communication and Interaction</h6>

                <p>
                  When a user registers to have access to the Site, when we send
                  a Communication to a user, or when the user accesses, responds
                  to, or declines to open a Communication from us, completes a
                  form to communicate with us, or when a user contacts us, we
                  automatically collect and store certain information about the
                  user, the user’s device (computer, tablet, smart phone) and
                  the user’s activities, such as:
                </p>

                <ul>
                  <li>The user's name and contact information;</li>
                  <li>
                    Information that the user provides or comments that the user
                    makes;
                  </li>
                  <li>The nature of the Communication;</li>
                  <li>
                    The purpose of the interaction, and the action we took in
                    response to the user’s inquiry or request;
                  </li>
                  <li>
                    Whether the user opened our Communication or ignored it;
                  </li>
                  <li>
                    Any action the user takes upon receipt of such Communication
                    (for example, whether the user registers to attend an event
                    that we offer, or request to receive documentation).
                  </li>
                </ul>

                <h6>Social Media</h6>
                <p>
                  Our Site includes social media features (such as the Facebook
                  “Like” button). These features may collect your IP address and
                  which page you are visiting our Site, and may set a cookie to
                  enable the feature to function properly. Social media features
                  and widgets may be hosted by a third party or directly on our
                  Site. Your interactions with these features are governed by
                  the privacy policy of the company providing the feature.
                </p>

                <h6>Information We Obtain from Third Parties</h6>
                <p>
                  We may obtain information from third parties, for example:
                </p>
                <ul>
                  <li>
                    Third parties with whom we do business, such as strategic
                    business partners, service providers, public databases, or
                    other third parties;
                  </li>
                  <li>
                    Social media services to which our Site or Apps might be
                    linked.
                  </li>
                </ul>

                <h6>Other Information</h6>
                <p>We may collect and use:</p>
                <ul>
                  <li>
                    Information about the user’s activities to infer the user’s
                    assumed interests;
                  </li>
                  <li>
                    information about the user that is available in public
                    records or other publicly available databases.
                  </li>
                </ul>
              </span>
            </li>

            <li>
              <h5>How We Collect Information</h5>
              <span>
                <p>
                  We (and our service providers) collect information in a
                  variety of ways, such as through the methods identified below:
                </p>
                <ul>
                  <li>
                    From the user’s browser: Certain information is collected by
                    most browsers or automatically through a user’s device, such
                    as the screen resolution, operating system name and version,
                    device manufacturer and model, language, browser type and
                    version;
                  </li>
                  <li>
                    Through a user’s activities offline, for example when a user
                    responds to a newsletter or interacts with us by telephone;
                  </li>
                  <li>
                    Through a user’s social media account if it is connected to
                    the user’s account on our Site.
                  </li>
                </ul>
                <p>
                  We, our service providers, and any ad networks who help us
                  deliver relevant ads to users may use cookies, pixel tags
                  (also known as web beacons and clear GIFs), and other similar
                  technologies to measure and track general user activities on
                  our Site, and on third party websites. Cookies are small text
                  files containing a string of alphanumeric characters. We may
                  use both session cookies and persistent cookies. A session
                  cookie disappears after you close your browser. A persistent
                  cookie remains after you close your browser and may be used by
                  your browser on subsequent visits to the Service.
                </p>
                <p>
                  We use Google Analytics to analyze how people use our Site and
                  compile statistical reports. Such services may also collect
                  information about a person’s use of other websites.
                </p>
              </span>
            </li>

            <li id="cookies-section">
              <h5>Cookies and other Technologies</h5>
              <span>
                <p>
                  We use cookies, pixel tags, web beacons, and other
                  technologies to:
                </p>
                <ul>
                  <li>
                    Collect information such as browser type, time spent on the
                    Services, pages visited, and traffic data;
                  </li>
                  <li>
                    Recognize a user when the user moves from page to page, or
                    returns to use the Service;
                  </li>
                  <li>Display information more effectively;</li>
                  <li>
                    Store, on a user’s device, information about that user’s
                    preferences and settings;
                  </li>
                  <li>
                    Gather statistical information about the use of the Service
                    to understand how our Service is used, and improve design
                    and functionality;
                  </li>
                  <li>Tailor our offers to apparent interests of our users;</li>
                  <li>
                    Track responses to our marketing and advertising campaigns;
                    and
                  </li>
                  <li>For security purposes.</li>
                </ul>
                <p>
                  Some of these cookies and other technologies are used by
                  Google Analytics to collect and analyze information about the
                  use of the Service, and provide us with reports on the
                  activities and trends. You can learn about Google’s practices
                  by visiting www.google.com/policies/privacy/partners and
                  opt-out from the collection of your information by downloading
                  the Google Analytics opt-out browser add-on available at{" "}
                  <a
                    className="links"
                    href="https://tools.google.com/dlpage/gaoptout"
                    target="_blank"
                  >
                    https://tools.google.com/dlpage/gaoptout
                  </a>
                  .
                </p>
                <p>
                  You may choose to block certain categories of cookies. Most
                  browsers contain information on how to control or delete
                  cookies. These settings will typically be found in the
                  “options,” “help,” or “preferences” menu of a browser. You may
                  also wish to refer to{" "}
                  <a
                    className="links"
                    href="http://www.allaboutcookies.org/manage-cookies"
                    target="_blank"
                  >
                    http://www.allaboutcookies.org/manage-cookies
                  </a>
                  .
                </p>
                <p>
                  Please note that blocking or disabling certain cookies may
                  interfere with certain functionalities of some of our Service.
                  Further, while disabling a cookie may prevent the collection
                  of information in the future, it does not prevent the use of
                  information collected before the cookie was disabled.
                </p>
              </span>
            </li>

            <li>
              <h5>Use of Information</h5>
              <span>
                <p>
                  In addition to some of the specific uses of information that
                  are described in this Privacy Notice, we may use the
                  information that we have collected to:
                </p>
                <ul>
                  <li>Send administrative information to the user;</li>
                  <li>
                    Improve the user’s experience, such as by personalizing
                    Content to the user’s preferences or interests or expedite
                    the processing and completion of a transaction;
                  </li>
                  <li>
                    Remember the user’s preference, such as language, font size,
                    when using our Site;
                  </li>
                  <li>Remember the user’s interests;</li>
                  <li>Personalize the user’s online experience;</li>
                  <li>
                    Administer our Site, diagnose technical problems, and
                    otherwise manage our business;
                  </li>
                  <li>Facilitate the user’s use of the Service;</li>
                  <li>
                    Allow the user to navigate or browse through our Site
                    quickly and efficiently;
                  </li>
                  <li>
                    Personalize the user’s experience by presenting content that
                    is tailored to that specific user based on what we know
                    about that user;
                  </li>
                  <li>
                    Keep records of contact information and correspondence;
                  </li>
                  <li>Send marketing information;</li>
                  <li>Communicate with the user about our activities;</li>
                  <li>
                    Send invitations to events that may be of interest to the
                    user in accordance with the user’s preferences or apparent
                    interests;
                  </li>
                  <li>
                    Facilitate social sharing functionality, such as sharing
                    content, through social media sites, e.g., Facebook and/or
                    Twitter;
                  </li>
                  <li>
                    Allow the user to send to or share content with a friend
                    through the Site; By using this feature, you guarantee that
                    you have the right to use and provide us the names and email
                    addresses you submit;
                  </li>
                  <li>
                    Allow users to participate in events and administer these
                    activities. Some of these activities have additional rules,
                    which could contain additional information about how we use
                    and disclose information about users. We recommend that you
                    read these rules carefully;
                  </li>
                  <li>
                    Improve, test, and monitor the effectiveness of the Service;
                  </li>
                  <li>
                    Develop features on the Service that may be of interest to
                    you;
                  </li>
                  <li>
                    Optimize our marketing efforts, for example to compile
                    statistics regarding the users of the Service, to identify
                    usage trends, and to determine the effectiveness of our
                    promotional campaigns;
                  </li>
                  <li>
                    Perform data analysis, audit, security, fraud monitoring and
                    prevention;
                  </li>
                  <li>
                    Enhance, improve, or modify our Site; identify usage trends;
                    determine the effectiveness of our promotional campaigns; or
                    operate or expand our business activities; and
                  </li>
                  <li>
                    Help diagnose server problems, and detect fraud and spam
                    behavior.
                  </li>
                </ul>
              </span>
            </li>

            <li>
              <h5>Sharing of Information</h5>
              <span>
                <p>
                  Some of the technologies used on our Service are managed by
                  third parties. The practices of some of these third parties
                  are subject to the third parties’ privacy policies over which
                  we have no control. We encourage you to read their privacy
                  policies. Third parties who may have access to information may
                  include:
                </p>
                <h6>Third-Party Services</h6>
                <p>
                  We share information with our suppliers, subcontractors, and
                  other third parties who provide services to us (collectively
                  “service providers”) in connection with advertising, hosting,
                  data analytics, information technology and infrastructure,
                  email delivery, auditing, and other related activities. Our
                  service providers are given only the information they need to
                  perform their designated functions.
                </p>

                <h6>Partners and Sponsors</h6>
                <p>
                  Some of our content is offered or promoted in conjunction with
                  a partner or sponsor. We may share user information with these
                  parties. In this context, we may provide to our advertisers
                  information about our visitors and users, in aggregated from,
                  to assist in identifying the demographics and interests of our
                  audience.
                </p>

                <h6>Social Media</h6>
                <p>
                  The Service may include links to third party websites and
                  social media services where a user is able to post comments,
                  reviews or other information. The use of these third party
                  websites and social media services may result in the
                  collection and sharing of information about a user with these
                  third party websites and social media services. A user’s
                  information may be disclosed to anyone to whom the user sends
                  a message through the Services (for example when forwarding a
                  link), or when commenting on a message board, or by
                  transmitting information through our social networking
                  buttons. Please note that any information that is posted or
                  disclosed through these social media services may be available
                  to us, or to other users of that service or the public. We
                  recommend proceeding with caution when using these features.
                </p>

                <h6>Responding to legal requests and preventing harm</h6>
                <p>
                  We may disclose information about a user to a third-party in
                  response to a facially valid request from a government agency
                  or a private litigant in the form of a subpoena, court order
                  or search warrant, or where we believe, in good faith, that it
                  is necessary to do so for the purposes of a civil action,
                  criminal investigation, other legal matter.
                </p>

                <h6>Change of Control</h6>
                <p>
                  We may disclose and provide information about our users to a
                  third-party in the event of reorganization, merger, sale,
                  joint venture, assignment, transfer, or other disposition of
                  all or any of our business, assets, or stock, including in
                  connection with bankruptcy or similar proceedings.
                </p>

                <h6>Aggregated, Anonymized, or Statistical Information</h6>
                <p>
                  We generate and use aggregated, anonymized, or statistical
                  information about the use of our Services. We may provide this
                  information to third parties such as advisers or consultants
                  for research, analytical or strategic purposes. This
                  information is not intended to allow the identification of any
                  specific user of our Service.
                </p>
              </span>
            </li>

            <li>
              <h5>Your Advertising Choices</h5>
              <span>
                <p>
                  radivision.com is provided at no cost to users. In order to
                  finance some of the operations of the Site, we have opted to
                  make it available to advertisements by third parties. Some
                  advertisement methodology may be targeting a broad market or
                  audience, may be directed to a specific individual, and may be
                  based on the specific apparent interest of the user of a
                  specific device (e.g. woman, between 28 and 34, interested in
                  sweater, with polka dot pattern). We may also use aggregated
                  and non-identifying information to deliver advertisements
                  targeted to a certain audience. Based upon the aggregated
                  information we have collected about users, we display the
                  advertisement to the intended audience based on these general
                  criteria.
                </p>
                <p>
                  Some of our advertising partners are members of the Network
                  Advertising Initiative (
                  <a
                    className="links"
                    href="http://optout.networkadvertising.org/?c=1#!/"
                    target="_blank"
                  >
                    http://optout.networkadvertising.org/?c=1#!/
                  </a>
                  ) or the Digital Advertising Alliance (
                  <a
                    className="links"
                    href="http://optout.aboutads.info/?c=2&lang=EN"
                    target="_blank"
                  >
                    http://optout.aboutads.info/?c=2&lang=EN
                  </a>
                  ). If you do not wish to receive personalized ads, please
                  visit their opt out pages to learn about how you may opt out
                  of receiving web-based personalized ads from member companies.
                  You can access any settings offered by your mobile operating
                  system to limit ad tracking, or you can install the AppChoices
                  mobile app to learn more about how you may opt out of
                  personalized ads in mobile apps.
                </p>
              </span>
            </li>

            <li>
              <h5>Links to Third-Party Sites or Applications</h5>
              <span>
                <p>
                  The Service may allow you to directly access applications or
                  services operated by third parties.
                </p>
                <p>
                  These links are provided for your convenience only and should
                  be used at your discretion.
                </p>
                <p>
                  These links do not constitute sponsorship, endorsement, or
                  approval of the content, policies or practices of such
                  third-parties’ applications.
                </p>
                <p>
                  We are not responsible for any information these
                  third-parties’ services may obtain and we do not oversee their
                  policies or practices. You should review their privacy notices
                  to understand how they may use your information. This Privacy
                  Notice does not apply to information provided to or gathered
                  by the third parties that operate them.
                </p>
              </span>
            </li>

            <li>
              <h5>Information Security</h5>
              <span>
                <p>
                  We take reasonable measures to protect any personal
                  information we may hold in order to prevent loss, misuse,
                  unauthorized access, disclosure, alteration and destruction.
                  However, because our Service is hosted electronically, we can
                  make no guarantees as to the security or privacy of user
                  information.
                </p>
                <p>
                  You should not disclose any username or password required to
                  access the Service to any third party. You will be responsible
                  for any use of radivision.com under your username / password.
                </p>
              </span>
            </li>

            <li>
              <h5>Consent to Transfer of Information</h5>
              <span>
                <p>
                  If you are located outside the United States, please be aware
                  that information we collect about you will be transferred for
                  processing to the United States or other countries or
                  jurisdictions that may not have the same data protection laws
                  as your jurisdiction.
                </p>
                <p>
                  By registering for, and using the Service, you consent to the
                  transfer and storage of your personal information to the U.S.
                  or to any other country in which we or our service providers
                  maintain facilities.
                </p>
              </span>
            </li>

            <li>
              <h5>Your Privacy Choices</h5>
              <span>
                <p>
                  Users have numerous choices regarding information pertaining
                  to them.
                </p>
                <h6>Cookies</h6>
                <p>
                  You may choose to block our Site and other sites from setting
                  certain categories of cookies in the future. if you choose to
                  do so, you should know that large parts of our Site will not
                  work if you block our cookies. You may wish to refer to{" "}
                  <a
                    className="links"
                    href="http://www.allaboutcookies.org/browsers/index.html"
                    target="_blank"
                  >
                    http://www.allaboutcookies.org/browsers/index.html
                  </a>{" "}
                  for information on commonly used browsers.
                </p>
                <h6>
                  Please note that blocking or disabling certain cookies may
                  interfere with certain functionalities of some parts of our
                  Site. Further, while disabling a cookie may prevent the
                  collection of information in the future, it does not prevent
                  the use of information collected before the cookie was
                  disabled.
                </h6>

                <h6>Social Media</h6>
                <p>
                  You can edit or remove the permissions you have granted to
                  Radivision to use information from your social media accounts
                  by using the application privacy settings on your social media
                  account. If you have signed in on our Site through Facebook
                  connect, Facebook likely sets cookies in your browser. You can
                  modify or change those cookies through the settings on your
                  Facebook account.
                </p>

                <h6>Electronic Communications</h6>
                <p>
                  If you receive commercial electronic communications from us,
                  you can unsubscribe from the receipt of future commercial
                  electronic communications from us by clicking on the
                  “unsubscribe link” provided in such communications. Please
                  also note that if you do opt out of receiving emails from us,
                  we may still send you important administrative messages (such
                  as updates about your account or service changes), and you
                  cannot opt out from receiving these messages.
                </p>

                <h6>Use of Data</h6>
                <p>
                  You can request that we stop using your information to provide
                  you services, please contact us at hello@radivision.com , and
                  we will do so within ten (10) days of receiving your notice.
                </p>
              </span>
            </li>

            <li>
              <h5> Children</h5>
              <span>
                <p>
                  This Service is a general audience service. Our content is not
                  directed towards children who are under the age of 13. We do
                  not knowingly collect personal information from children under
                  the age of 13. If we become aware that a child under the age
                  of 13 has provided us with personal information without
                  parental consent, that information will be deleted. Parents
                  who have questions about personal information that may have
                  been submitted by a child under the age of 13 should email us
                  at the address provided in the “How to Contact Us” section.
                </p>
                <p>
                  By using the Service, you represent that you are at least 13
                  years old.
                </p>
              </span>
            </li>

            <li>
              <h5>Your California Privacy Rights</h5>
              <span>
                <p>
                  California law requires website operators to make certain
                  disclosures:
                </p>

                <h6>Data Sharing</h6>
                <p>
                  We will not share any personal information about a user with
                  outside third-parties for their direct marketing purposes to
                  the extent prohibited by California law, unless the user
                  agrees otherwise and in writing.
                </p>

                <h6>Do-Not-Track</h6>
                <p>
                  Some browsers give individuals the ability to communicate that
                  they wish not to be tracked while browsing on the Internet.
                  California law requires that operators of online services
                  disclose whether they comply with do-not-track request. The
                  Internet industry has not yet agreed on a definition of what
                  “Do Not Track” means, how compliance with “Do Not Track” would
                  be measured or evaluated, or a common approach to responding
                  to a “Do Not Track” signal. Consequently, due to the lack of
                  guidance, we have not yet developed features that would
                  recognize or respond to browser-initiated Do Not Track signals
                  in response to California law.
                </p>
              </span>
            </li>

            <li>
              <h5>How to Contact Us</h5>
              <span>
                <p>
                  <strong>
                    If you have any questions about this Privacy Notice, please
                    contact us by email at{" "}
                    <a className="links" href="mailto:hello@radivision.com">
                      hello@radivision.com
                    </a>
                  </strong>
                </p>
              </span>
            </li>
          </ol>
        </div>
      </div>
    );
  }
}
